import { useState, useEffect } from "react";
import { supabase } from "./supabase";

// ─── PASTE THIS OBJECT at the top of your App.js, just above the MODULES array ───

const LESSONS_CONTENT = {
  // Module 1, Week 1
  "1-0": {
    sections: [
      {
        heading: "The Most Important Distance in Your Life",
        body: [
          "There is a version of you that already exists. She is not a fantasy — she is the logical, inevitable result of who you become when you stop abandoning yourself. She wakes up with intention. She moves through the world with ease and confidence. She has done the work, built the habits, and designed the life. She is not waiting for permission. She is not playing small.",
          "Right now, there is a gap between who you are today and who she is. That gap is not a flaw. It is not evidence of failure. It is simply the distance you have not yet traveled — and this course is the map.",
        ],
        keyIdea: "The gap between who you are and who you're becoming is not a problem to fix. It's a direction to move in.",
      },
      {
        heading: "Why Most People Never Close the Gap",
        body: [
          "It is not because they lack desire. Everyone desires a better life. It is not because they lack intelligence or talent. The women who stay stuck are often the most capable people in the room. The gap stays open for three specific reasons:",
        ],
        bullets: [
          "They never define her clearly enough. A vague vision produces vague results. \"I want to be healthier\" is not a vision. \"I am a woman who moves her body five mornings a week because it is the foundation of everything else\" — that is a vision. The specificity is the power.",
          "They try to change behavior without changing identity. You cannot sustain actions that feel foreign to who you believe you are. If you believe you are someone who 'always falls off track,' you will fall off track — not because you are weak, but because you are being consistent with your self-concept. Identity must change first.",
          "They rely on motivation instead of systems. Motivation is a feeling. Feelings are temporary. Systems are structures. When the feeling is gone, the structure holds you. This course is fundamentally a systems course disguised as a personal growth course.",
        ],
      },
      {
        heading: "Writing Her Into Reality",
        body: [
          "One of the most powerful exercises in this entire course is deceptively simple: write about her in the present tense, as if she already exists. Not 'I want to be' or 'someday I will' — but 'I am.' This is not magical thinking. This is neurological priming. When you write in present tense, your brain begins to close the gap between the person you're describing and the person you currently are.",
          "She is specific. She has a morning. She has a body that feels a certain way. She has a career that looks a specific way. She has friendships that feel a specific way. The more detail you give her, the more real she becomes — and the more your daily choices will begin to orient toward her without you having to force it.",
        ],
        callout: {
          label: "This Week's Anchor Thought",
          text: "Every decision you make this week is either a vote for the woman you are becoming or a vote for the woman you've been. Neither is permanent. But they compound.",
        },
      },
      {
        heading: "The Practice of Identity-Voting",
        body: [
          "James Clear, in Atomic Habits, introduces the concept of identity-based habits: the idea that every action you take is a vote for the type of person you believe yourself to be. Miss a workout? One vote against. Show up anyway when you didn't feel like it? Two votes for. The goal is not perfection. The goal is to win the majority of votes over time.",
          "This week, you are not being asked to overhaul your life. You are being asked to do one thing: write her into existence with enough clarity that you know, in any given moment, what she would do. When you don't feel like cooking at home — what would she do? When you want to skip the workout — what would she do? When you catch yourself in negative self-talk — what would she do?",
          "She becomes your compass. Not a source of shame when you fall short — a direction to return to, always.",
        ],
        reflection: "Sit quietly for 10 minutes before writing your Future Self Letter assignment. Ask yourself: What does a Tuesday morning look like for her? How does she feel in her body? How does she walk into a room? What has she stopped apologizing for? What has she started doing consistently? Let the image become clear before you write.",
      },
    ],
  },

  // Module 1, Week 2
  "1-1": {
    sections: [
      {
        heading: "The Foundation Beneath the Foundation",
        body: [
          "Values are the invisible architecture of your life. They are operating in the background of every decision you make — whether you have consciously chosen them or not. When your life feels off, when you feel vaguely dissatisfied or constantly conflicted, it is almost always because you are living out of alignment with your actual values. Not the values you think you should have. Your actual ones.",
          "This distinction matters enormously. Many women carry inherited values — values absorbed from family, culture, religion, social media — that they have never examined or consciously chosen. They live by these borrowed values and wonder why their life doesn't feel like their own. This week is about reclaiming authorship.",
        ],
        keyIdea: "You cannot build a life that feels like yours on a foundation of values that aren't.",
      },
      {
        heading: "How to Find Your Actual Values",
        body: [
          "Your real values are revealed not in what you say you believe, but in what you actually do with your time, money, energy, and attention. These are the most honest indicators of what you truly prioritize. Here is how to excavate them:",
        ],
        bullets: [
          "Look at your calendar. Where does your time actually go without you planning it? That is where your current values live — whether you like them or not.",
          "Look at your bank statements. You vote with your money every day. What are you consistently paying for? What are you consistently not investing in?",
          "Notice what angers you. Anger is often a signal that a core value has been violated. If dishonesty in others makes you furious, integrity is likely a core value.",
          "Notice what you envy. Envy points directly at what you want. Before judgment, get curious: what does this envy reveal about what I actually value?",
          "Look at your non-negotiables. What would you never compromise, no matter the reward? The things you protect fiercely are the things you truly value.",
        ],
      },
      {
        heading: "The Alignment Audit",
        body: [
          "Once you have identified your top five values, the next step is ruthlessly honest: are you actually living them? For each value, ask yourself two questions. First, on a scale of one to ten, how present is this value in my current daily life? Second, what is one specific way I am honoring this value — and one specific way I am betraying it?",
          "This audit is not meant to generate shame. It is meant to generate clarity. A gap between your values and your life is not a character flaw — it is information. It tells you exactly where to focus your energy in the months ahead.",
        ],
      },
      {
        heading: "When Values Conflict",
        body: [
          "One of the most underexplored sources of internal conflict is competing values. Perhaps you deeply value both freedom and security. Or both adventure and stability. Or both ambition and presence. These are not contradictions — they are tensions to be managed, not resolved.",
          "The woman who has done this work knows which value takes precedence in which context. She has thought it through in advance, so she is not paralyzed when the decision arrives. This is values architecture: not just knowing what you value, but knowing the hierarchy, the order, the structure that guides you when life becomes complicated.",
        ],
        callout: {
          label: "A Note on Inherited Values",
          text: "If you were raised in a family or culture with strong value systems, some of those values may be genuinely yours — and some may be borrowed costumes you have been wearing for decades. The question is not 'are these values good or bad?' The question is: 'Did I choose these? Do they feel true to who I actually am?'",
        },
        reflection: "Write down the five values that emerged from your excavation. For each one, complete this sentence without editing yourself: 'I know this is a real value for me because...' Then write: 'The way I most consistently betray this value is...' Notice what comes up. That tension is the work.",
      },
    ],
  },

  // Module 1, Week 3
  "1-2": {
    sections: [
      {
        heading: "The Motivation Myth",
        body: [
          "You have been taught a lie about change. The lie is that if you want something badly enough, if you are motivated enough, disciplined enough, you will change your behavior. This is backwards. Motivation is the result of action, not the prerequisite for it. You do not wait until you feel like exercising to exercise. You exercise, and then you feel good about it, and then you want to do it again. The feeling follows the behavior. Not the other way around.",
          "This means that every time you wait to feel motivated before you begin, you are waiting for something that will only arrive after you have already started. This is the trap. And it is why people who rely on motivation cycle endlessly through starting and stopping, inspired and deflated, committed and fallen off.",
        ],
        keyIdea: "Motivation follows action. Stop waiting to feel ready. Begin, and the feeling will come.",
      },
      {
        heading: "The Three Layers of Behavior Change",
        body: [
          "Most people try to change their outcomes — they focus on results they want. Some go a layer deeper and try to change their processes. But the deepest and most durable layer of change is identity: who you believe yourself to be. Identity-based change asks: 'Who is the person who already has this result — and how do I become her?'",
        ],
        bullets: [
          "Outcome-based: I want to run a marathon. (Result focus — fragile.)",
          "Process-based: I will train four days a week. (System focus — better.)",
          "Identity-based: I am a runner. (Identity focus — durable.)",
        ],
      },
      {
        heading: "Building the Evidence",
        body: [
          "You cannot simply decide to have a new identity and have it be true. Identity is built through evidence — the accumulation of small actions that prove to yourself who you are. Every time you follow through on something you said you would do, you deposit evidence into your self-concept. Every time you abandon it, you make a withdrawal. The goal is to make more deposits than withdrawals, consistently, until the evidence is undeniable.",
          "This is why starting small matters so much. A woman who commits to walking for ten minutes every morning and does it for thirty days has built something far more valuable than grand plans abandoned: proof that she keeps her word to herself. That proof is the foundation of identity change.",
        ],
      },
      {
        heading: "The 'She Would' Practice",
        body: [
          "This is the single most practical tool from this entire module. In any moment of resistance — when you don't feel like doing the thing, when the couch is calling, when the excuse feels valid — pause and ask one question:",
          "\"What would she do?\"",
          "Not 'what should I do' — which triggers resistance and shame. Not 'what do I feel like doing' — which will usually be the easier thing. But: what would the version of me who has already built this life do, right now, in this exact moment?",
          "Then do that. Not perfectly. Not heroically. Just do it. Even a 60% version of it. Because the doing is the identity-building. The doing is the vote. And the votes compound into a landslide over six months.",
        ],
        callout: {
          label: "On Perfectionism",
          text: "Perfectionism is not high standards. Perfectionism is fear wearing a very convincing costume. The woman you are becoming is not perfect — she is consistent. She shows up at 70% when 100% is not available. She does the imperfect version rather than waiting for the perfect conditions that never fully arrive.",
        },
        reflection: "Identify one habit you have tried and failed to build repeatedly. Now reframe it as an identity statement: not 'I am trying to...' but 'I am a person who...' Write three pieces of past evidence — however small — that already support this identity. Then commit to one action this week that casts another vote for it.",
      },
    ],
  },

  // Module 1, Week 4
  "1-3": {
    sections: [
      {
        heading: "Structure Is Not a Cage. It Is a Container.",
        body: [
          "There is a pervasive myth that freedom means having no structure — that the truly free person flows spontaneously through their days, unbound by routine. This is romantic and it is false. The most creative, productive, joyful, and purposeful people in the world are almost universally people with strong personal operating systems. Structure is not what limits your freedom. It is what makes it possible.",
          "Without structure, your days are governed by the loudest, most urgent thing in front of you — which is almost never the most important thing. Without structure, your energy is spent on decisions about what to do next instead of on the doing itself. Your operating system removes that friction.",
        ],
        keyIdea: "You do not rise to the level of your goals. You fall to the level of your systems.",
      },
      {
        heading: "The Three Pillars of Your Operating System",
        bullets: [
          "The Morning Ritual. The first 60–90 minutes of your day belong to you, before the world's demands arrive. This is not about productivity. It is about state. How you begin your day sets the emotional, physical, and mental tone for everything that follows. A morning ritual that includes movement, nourishment, reflection, and intention is the highest-leverage investment you can make.",
          "The Evening Wind-Down. How you end your day determines how you begin the next one. An evening routine that includes a brief reflection, preparation for tomorrow, and a transition away from screens protects your sleep — and your sleep protects everything else.",
          "The Weekly Review. Once a week — ideally Sunday — you sit down with your life for fifteen minutes. You look at what happened, what didn't, what you want from the week ahead. This fifteen minutes is the difference between drifting through weeks and designing them.",
        ],
      },
      {
        heading: "The Sunday Review Template",
        body: [
          "Each Sunday, answer these questions in writing — not just in your head:",
        ],
        bullets: [
          "What did I do this week that my future self is proud of?",
          "What did I let slip — and what was underneath that choice?",
          "How did my body feel this week? What does it need more or less of?",
          "Did I move toward my career and financial vision? How specifically?",
          "Did I nourish at least one important relationship with real attention?",
          "Did I do something for joy, beauty, or creativity?",
          "What is my single most important focus in each pillar for next week?",
          "Am I being the version of her I described in Week 1? Where am I drifting?",
        ],
      },
      {
        heading: "A Word on Consistency vs. Perfection",
        body: [
          "Your operating system will not run perfectly every day. You will have mornings where the ritual is compressed to ten minutes. You will have Sundays where the review doesn't happen. This is not failure — this is life. The standard is not perfection. The standard is return. How quickly do you return to your system after it breaks? That is the muscle you are building. Every return strengthens it.",
        ],
        callout: {
          label: "The Module 1 Integration",
          text: "By the end of this week you should have: a Future Self Letter, a completed 8-Pillar Audit, your five core values with an honest alignment assessment, seven consecutive days of your morning ritual logged, your three self-sabotage patterns named, and four Sunday reviews completed. If any are incomplete — do not move to Module 2. Complete them first. The foundation is everything.",
        },
        reflection: "Write out your ideal morning ritual in sequence, from the moment your alarm goes off to the moment you begin your workday. Be specific about timing and activities. Then write your evening wind-down. Now ask: which of these elements is most important to protect, even on the hardest days? That is your anchor habit for this month.",
      },
    ],
  },
};


const MODULES = [
  {
    id: 1, emoji: "✦", color: "#4A5200", bg: "#F5FAE0", accent: "#A8C400",
    title: "Foundation & Identity", subtitle: "Who Is She, Really?",
    weeks: "Weeks 1–4", focus: "Self-Knowledge & Vision Architecture",
    description: "Before tactics, before habits — you must know who she is. This month is about building the foundation everything else rests on: your vision, your values, your daily operating system.",
    objectives: [
      "Write a clear, emotionally resonant vision for your life across all 8 pillars",
      "Identify your core values and how your life aligns or misaligns with them",
      "Understand the identity-behavior gap and begin closing it daily",
      "Design your personal operating system: morning ritual, weekly review",
      "Name and begin interrupting your 3 most common self-sabotage patterns",
    ],
    lessons: [
      { week: 1, title: "The Gap", desc: "Who you are now vs. who she is. Writing her into reality with full presence." },
      { week: 2, title: "Your Values Architecture", desc: "What you actually believe, prioritize, and protect — not what you think you should." },
      { week: 3, title: "Identity-Based Habits", desc: "Why motivation fails and identity wins. The James Clear framework applied to your life." },
      { week: 4, title: "Your Operating System", desc: "Designing the daily and weekly structure that holds everything else together." },
    ],
    assignments: [
      { type: "reflect", title: "Future Self Letter", desc: "Write 500 words from her perspective — present tense, fully alive. Where is she? How does she feel?" },
      { type: "act", title: "8-Pillar Life Audit", desc: "Rate all 8 pillars (1–10), set targets, write one word for your vision in each area." },
      { type: "reflect", title: "Values Excavation", desc: "Identify your top 5 core values. Write a paragraph on how you honor each — or don't." },
      { type: "act", title: "7-Day Morning Ritual", desc: "Design and execute your morning ritual for 7 consecutive days. Track how each day feels." },
      { type: "reflect", title: "The Stories You Tell", desc: "Journal: What are the 3 stories you tell yourself that keep you playing small?" },
      { type: "act", title: "4-Week Sunday Review", desc: "Implement your weekly review every Sunday for the full month. Write what you notice shifting." },
    ],
    checkin: "Can you describe her without hesitation? Do you have a daily structure in place? Rate your clarity (1–10). What surprised you most about yourself this month?",
  },
  {
    id: 2, emoji: "🌿", color: "#2A5A40", bg: "#E8F5EE", accent: "#4A9E6E",
    title: "Body & Wellbeing", subtitle: "Your Most Important Asset",
    weeks: "Weeks 5–8", focus: "Health, Energy & Physical Confidence",
    description: "Your body is the vessel for everything else. When you feel physically strong and at home in your skin — every other area elevates. This month is about building sustainable care, not punishment.",
    objectives: [
      "Build a sustainable, enjoyable movement practice — not punishment, power",
      "Understand your unique body: energy patterns, hormones, sleep architecture",
      "Create a nourishment framework that fuels ambition without restriction or guilt",
      "Develop a beauty and self-care ritual that feels luxurious, not obligatory",
      "Learn to read your body's signals and respond with care instead of override",
    ],
    lessons: [
      { week: 5, title: "Movement as Identity", desc: "Find the workout that makes you feel like her. Movement you love vs. movement you endure." },
      { week: 6, title: "Fuel & Nourishment", desc: "Eating for energy, clarity, and longevity. Not aesthetics alone — performance and vitality." },
      { week: 7, title: "Sleep & Recovery", desc: "Your secret competitive advantage. Protecting sleep as a non-negotiable pillar of success." },
      { week: 8, title: "Beauty as Self-Respect", desc: "Skincare, body care, and rituals that signal to yourself that you matter." },
    ],
    assignments: [
      { type: "reflect", title: "Body Vision", desc: "Write your Body Vision — not a size, a feeling. What does thriving in your body actually mean to you?" },
      { type: "act", title: "4x/Week Movement Commitment", desc: "Commit to a movement form 4x per week for the entire month. Log your consistency each week." },
      { type: "reflect", title: "Energy Audit", desc: "Track your energy for 2 weeks: what lifts you, what depletes you, what drains without restoring." },
      { type: "act", title: "21-Day Skincare Ritual", desc: "Build your AM + PM skincare routine. Execute it for 21 consecutive days without exception." },
      { type: "reflect", title: "Body Relationship Journal", desc: "What is your relationship with your body? Where does it need healing, patience, or celebration?" },
      { type: "act", title: "Health Appointments", desc: "Schedule all annual health appointments this week: physical, dental, dermatologist. Put them in your calendar." },
    ],
    checkin: "How does your body feel compared to Week 1? What movement practice stuck? What health habits are now automated? Where are you still resisting care for yourself?",
  },
  {
    id: 3, emoji: "💼", color: "#3A3A2A", bg: "#F8F5E8", accent: "#8C8430",
    title: "Career & Money", subtitle: "Building the Life You Can Fund",
    weeks: "Weeks 9–12", focus: "Income, Ambition & Financial Intelligence",
    description: "You are in your building years. The groundwork you lay now — skills, reputation, financial systems — compounds exponentially. This month is about strategic ambition and money mastery.",
    objectives: [
      "Define your career vision: what you want to be known for and excellent at",
      "Identify your most monetizable skills and build a 12-month development plan",
      "Understand personal finance fundamentals and begin building real wealth",
      "Ask for what you're worth — in salary, rate, title, and recognition",
      "Build financial systems that run automatically: savings, investing, budgeting",
    ],
    lessons: [
      { week: 9, title: "Career Clarity", desc: "Your value proposition, positioning, and 5-year trajectory. Knowing exactly where you're going." },
      { week: 10, title: "Income Architecture", desc: "Salary negotiation, rates, raises, and side income. Asking for what you deserve." },
      { week: 11, title: "Money Foundations", desc: "Budgeting, emergency fund, debt payoff, Roth IRA. Building in the right order." },
      { week: 12, title: "Wealth Building", desc: "Index funds, compound interest, net worth tracking. Your money working while you sleep." },
    ],
    assignments: [
      { type: "reflect", title: "Professional Vision Statement", desc: "Write your vision: what you do, for whom, why it matters — and what you want to be known for." },
      { type: "act", title: "Skills Development Plan", desc: "Identify 3 skills to develop this year. Research one course, book, or mentor for each. Take action on one." },
      { type: "reflect", title: "Money Story Journal", desc: "What is your earliest money memory? What beliefs did it create? How do they show up today?" },
      { type: "act", title: "Complete Monthly Budget", desc: "Build a complete monthly budget and track every single dollar for 30 days. No exceptions." },
      { type: "reflect", title: "Net Worth & Targets", desc: "Calculate your current net worth. Set honest 12-month, 3-year, and 5-year financial targets." },
      { type: "act", title: "Roth IRA Setup", desc: "Open (or optimize) a Roth IRA and set up automatic contributions — however small. Do it this week." },
    ],
    checkin: "Do you have a clear career direction? Have you built a budget? Do you know your net worth? Where does money still feel scary, unclear, or emotionally loaded?",
  },
  {
    id: 4, emoji: "🤝", color: "#5A3A2A", bg: "#FBF0EA", accent: "#C07850",
    title: "Relationships & Community", subtitle: "The People Who Shape Your World",
    weeks: "Weeks 13–16", focus: "Friendships, Boundaries & Belonging",
    description: "Your mid-20s to 30s is when friendships require intentionality. Life gets busier, people scatter. Women who maintain rich social lives treat friendship as a practice — not a passive result.",
    objectives: [
      "Audit your current relationships with honesty and compassion",
      "Learn the art of maintaining deep friendships during a busy building season",
      "Identify where you want to expand community and take active steps",
      "Build and communicate healthy boundaries without guilt or over-explanation",
      "Become the kind of friend and community member you most admire",
    ],
    lessons: [
      { week: 13, title: "Relationship Audit", desc: "Your inner circle, growth friends, and draining dynamics. Seeing your social world clearly." },
      { week: 14, title: "The Art of Maintenance", desc: "How to keep friendships alive when life is full. The small moves that keep bonds strong." },
      { week: 15, title: "Boundaries as Self-Respect", desc: "What they are, why they're hard, how to set them — without guilt or a 3-paragraph explanation." },
      { week: 16, title: "Expanding Your World", desc: "Finding community, showing up, letting people in. How to build belonging from scratch." },
    ],
    assignments: [
      { type: "reflect", title: "Social Landscape Map", desc: "Map your current relationships: energizing, neutral, and draining. What do you notice?" },
      { type: "act", title: "4 Reconnections", desc: "Reach out to 4 people this month — one per week. Someone you've let drift. Rebuild the bridge." },
      { type: "reflect", title: "Influence Inventory", desc: "Who are the 3 people who most influence how you see yourself? Is that influence serving you?" },
      { type: "act", title: "Host a Gathering", desc: "Host one gathering, however small — a dinner, a walk, a coffee morning. You be the connector." },
      { type: "reflect", title: "Boundary Identification", desc: "Identify one boundary you need to set. Write it out fully: what it is, why it matters, how you'll say it." },
      { type: "act", title: "Join Something New", desc: "Join one group, class, or community aligned with who you're becoming. Attend at least twice." },
    ],
    checkin: "How does your social life feel now? Have you deepened any relationships? Have you set a boundary? What kind of community are you actively building — and where?",
  },
  {
    id: 5, emoji: "✨", color: "#3A3A5A", bg: "#EEEEF8", accent: "#6E6EAC",
    title: "Joy, Style & Environment", subtitle: "Living Beautifully on Purpose",
    weeks: "Weeks 17–20", focus: "Aesthetics, Creativity & Intentional Living",
    description: "Your external world is a reflection of your internal one. This month is about curating the life around you — your style, your spaces, your hobbies, your adventures — to match who you're becoming.",
    objectives: [
      "Define your personal style in words and begin embodying it daily without waiting",
      "Curate your physical environment to support your vision and nervous system",
      "Build a creative and joy practice that is protected, not optional",
      "Plan one meaningful travel experience and commit to making it happen",
      "Design a home and wardrobe that feel like the life you're actively building",
    ],
    lessons: [
      { week: 17, title: "Style as Identity", desc: "Defining your aesthetic, building a capsule wardrobe, dressing like her before you feel ready." },
      { week: 18, title: "Your Environment is a Message", desc: "Home curation, workspace design, sensory life — how space shapes your state every single day." },
      { week: 19, title: "The Joy Portfolio", desc: "Hobbies, creative outlets, and pleasure as productivity. Why fun is non-negotiable, not frivolous." },
      { week: 20, title: "Travel & Adventure", desc: "Planning with intention, budgeting for experiences, saying yes to the life that expands you." },
    ],
    assignments: [
      { type: "reflect", title: "Style in 5 Words", desc: "Define your style in exactly 5 words. Audit your closet — remove anything that isn't her." },
      { type: "act", title: "Space Refresh", desc: "Complete one room or space refresh. Declutter, add beauty, make it feel like where she lives." },
      { type: "reflect", title: "Joy Excavation", desc: "What does joy feel like in your body? Where has it gone quiet in your life? What brings it back?" },
      { type: "act", title: "Monthly Creative Practice", desc: "Commit to one hobby or creative practice for the month. Do it at minimum once per week." },
      { type: "reflect", title: "Beautiful Life Folder", desc: "Create a 'Beautiful Life' folder: images, places, and experiences that capture your vision." },
      { type: "act", title: "Book a Trip", desc: "Plan and book one trip — near or far. Research it, budget it, and commit. It's happening." },
    ],
    checkin: "Does your external world reflect who you're becoming? Where do you feel most at home in yourself now? What brings you the most uncomplicated, simple joy?",
  },
  {
    id: 6, emoji: "🌟", color: "#1A3A5A", bg: "#E8F0FA", accent: "#4A7AAC",
    title: "Integration & Legacy", subtitle: "She's Here. Now What?",
    weeks: "Weeks 21–24", focus: "Consolidation, Growth & Lifelong Systems",
    description: "Six months ago, you enrolled. Now you integrate. This final month is about honoring your transformation, building systems that sustain it, and writing the next chapter with full clarity.",
    objectives: [
      "Review and celebrate the full arc of your 6-month transformation with honesty",
      "Identify where you've grown most and where continued intentional work is needed",
      "Build your Year 2 vision based on who you are now — not who you were",
      "Create maintenance systems so your growth is self-sustaining and joyful",
      "Write the next chapter with confidence, clarity, and full commitment to yourself",
    ],
    lessons: [
      { week: 21, title: "The Full Audit", desc: "Review all 8 pillars. Where are you now vs. Month 1? See the full distance you've traveled." },
      { week: 22, title: "Sustainability", desc: "Building systems that maintain growth without constant effort. Automation, rituals, rhythms." },
      { week: 23, title: "The Next Chapter", desc: "Your Year 2 vision, goals, and who she's becoming next. The blueprint lives and grows." },
      { week: 24, title: "Ceremony & Commitment", desc: "Honoring how far you've come. Renewing your vow to yourself. Graduating with intention." },
    ],
    assignments: [
      { type: "reflect", title: "Response Letter", desc: "Re-read your Month 1 Future Self Letter. Write a response from who you are now. What changed?" },
      { type: "act", title: "Full Pillar Re-Audit", desc: "Complete the 8-Pillar Audit again. Compare ratings to Month 1. Document every shift." },
      { type: "reflect", title: "6-Month Lessons", desc: "What did this 6 months teach you about who you actually are? Write without filtering." },
      { type: "act", title: "Year 2 Operating System", desc: "Design your Year 2 system: updated morning ritual, weekly structure, habits, and goals." },
      { type: "reflect", title: "Year 2 Vision Letter", desc: "Write your Year 2 vision letter — longer, clearer, more specific than your first." },
      { type: "act", title: "Graduation Letter", desc: "Write a 'Graduation Letter' to your old self — what you want her to know. Make it beautiful." },
    ],
    checkin: "Where are you on the 1–10 scale in each pillar now? What habits are fully automated? What is the single most important thing you learned? What are you most proud of?",
  },
];

// ─── AUTH SCREEN ────────────────────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login"); // "login" | "signup" | "reset"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: "error"|"success", text }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        setMessage({ type: "success", text: "Check your email for a confirmation link, then come back to log in." });
      } else if (mode === "login") {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuth(data.user);
      } else if (mode === "reset") {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
        setMessage({ type: "success", text: "Password reset email sent. Check your inbox." });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "14px 16px", borderRadius: 8,
    border: "1px solid #E0E0D8", background: "#FAFAFA",
    color: "#1C1C1A", fontFamily: "Georgia, serif", fontSize: 15,
    outline: "none", boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "#F7F7F4",
      fontFamily: "Georgia, serif", padding: 20,
    }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 28, color: "#A8C400", marginBottom: 12 }}>✦</div>
          <div style={{ fontSize: 11, letterSpacing: 4, color: "#7A9200", fontWeight: "bold", marginBottom: 6 }}>THE LIFE DESIGN</div>
          <div style={{ fontSize: 26, color: "#FFFFFF", fontWeight: "bold" }}>Course Portal</div>
          <div style={{ fontSize: 13, color: "#6B6B65", fontStyle: "italic", marginTop: 6 }}>Your 6-month personal curriculum</div>
        </div>

        {/* Card */}
        <div style={{
          background: "#FFFFFF", border: "1px solid #E0E0D8",
          borderRadius: 12, padding: "36px 32px",
        }}>
          <div style={{ fontSize: 18, fontWeight: "bold", color: "#1C1C1A", marginBottom: 6 }}>
            {mode === "login" ? "Welcome back" : mode === "signup" ? "Create your account" : "Reset password"}
          </div>
          <div style={{ fontSize: 13, color: "#6B6B65", marginBottom: 28, fontStyle: "italic" }}>
            {mode === "login" ? "Your progress is waiting for you." : mode === "signup" ? "Start your transformation today." : "We'll send you a link to reset."}
          </div>

          {message && (
            <div style={{
              padding: "12px 16px", borderRadius: 6, marginBottom: 20,
              background: message.type === "error" ? "#3A1010" : "#0A2A1A",
              border: `1px solid ${message.type === "error" ? "#C04040" : "#40A060"}`,
              color: message.type === "error" ? "#F08080" : "#60C080",
              fontSize: 13, lineHeight: 1.5,
            }}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, letterSpacing: 2, color: "#6B6B65", fontWeight: "bold", marginBottom: 8 }}>EMAIL</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="you@example.com"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#A8C400"}
                onBlur={e => e.target.style.borderColor = "#E0E0D8"}
              />
            </div>

            {mode !== "reset" && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: "block", fontSize: 11, letterSpacing: 2, color: "#6B6B65", fontWeight: "bold", marginBottom: 8 }}>PASSWORD</label>
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  required placeholder={mode === "signup" ? "Minimum 6 characters" : "Your password"}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#A8C400"}
                  onBlur={e => e.target.style.borderColor = "#E0E0D8"}
                />
              </div>
            )}

            <button
              type="submit" disabled={loading}
              style={{
                width: "100%", padding: "14px", borderRadius: 8, border: "none",
                background: loading ? "#E0E0D8" : "linear-gradient(135deg, #A8C400, #7A9200)",
                color: loading ? "#999" : "#1C1C1A",
                fontSize: 13, fontWeight: "bold", letterSpacing: 2,
                cursor: loading ? "default" : "pointer",
                fontFamily: "Georgia, serif", transition: "all 0.2s",
              }}>
              {loading ? "Please wait..." : mode === "login" ? "LOG IN" : mode === "signup" ? "CREATE ACCOUNT" : "SEND RESET EMAIL"}
            </button>
          </form>

          {/* Mode switcher */}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #E0E0D8", textAlign: "center" }}>
            {mode === "login" && (<>
              <button onClick={() => { setMode("signup"); setMessage(null); }} style={{ background: "none", border: "none", color: "#7A9200", cursor: "pointer", fontSize: 13, fontFamily: "Georgia, serif" }}>
                No account? Sign up →
              </button>
              <br />
              <button onClick={() => { setMode("reset"); setMessage(null); }} style={{ background: "none", border: "none", color: "#9A9A94", cursor: "pointer", fontSize: 12, fontFamily: "Georgia, serif", marginTop: 8 }}>
                Forgot password?
              </button>
            </>)}
            {mode === "signup" && (
              <button onClick={() => { setMode("login"); setMessage(null); }} style={{ background: "none", border: "none", color: "#7A9200", cursor: "pointer", fontSize: 13, fontFamily: "Georgia, serif" }}>
                Already have an account? Log in →
              </button>
            )}
            {mode === "reset" && (
              <button onClick={() => { setMode("login"); setMessage(null); }} style={{ background: "none", border: "none", color: "#7A9200", cursor: "pointer", fontSize: 13, fontFamily: "Georgia, serif" }}>
                ← Back to login
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeModule, setActiveModule] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const [completedAssignments, setCompletedAssignments] = useState({});
  const [completedCheckins, setCompletedCheckins] = useState({});
  const [notes, setNotes] = useState({});
  const [checkinText, setCheckinText] = useState({});
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState(null);

  // ── Check auth state on mount ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load progress from Supabase when user logs in ──
  useEffect(() => {
    if (!user) return;
    async function loadProgress() {
      const { data, error } = await supabase
        .from("course_progress")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 = no rows found, which is fine for new users
        console.error("Error loading progress:", error);
        return;
      }
      if (data) {
        setCompletedAssignments(data.completed_assignments || {});
        setCompletedCheckins(data.completed_checkins || {});
        setNotes(data.notes || {});
        setCheckinText(data.checkin_text || {});
      }
    }
    loadProgress();
  }, [user]);

  // ── Debounced save to Supabase ──
  async function saveProgress(ca, cc, n, ct) {
    if (!user) return;
    if (saveTimeout) clearTimeout(saveTimeout);
    setSaving(true);
    const timeout = setTimeout(async () => {
      const payload = {
        user_id: user.id,
        completed_assignments: ca,
        completed_checkins: cc,
        notes: n,
        checkin_text: ct,
        updated_at: new Date().toISOString(),
      };
      // Upsert: insert if new, update if exists
      const { error } = await supabase
        .from("course_progress")
        .upsert(payload, { onConflict: "user_id" });
      if (error) console.error("Save error:", error);
      setSaving(false);
    }, 800); // wait 800ms after last keystroke before saving
    setSaveTimeout(timeout);
  }

  function toggleAssignment(modId, aIdx) {
    const key = `${modId}-${aIdx}`;
    const next = { ...completedAssignments, [key]: !completedAssignments[key] };
    setCompletedAssignments(next);
    saveProgress(next, completedCheckins, notes, checkinText);
  }

  function completeCheckin(modId) {
    const next = { ...completedCheckins, [modId]: true };
    setCompletedCheckins(next);
    saveProgress(completedAssignments, next, notes, checkinText);
  }

  function updateNote(key, val) {
    const next = { ...notes, [key]: val };
    setNotes(next);
    saveProgress(completedAssignments, completedCheckins, next, checkinText);
  }

  function updateCheckinText(modId, val) {
    const next = { ...checkinText, [modId]: val };
    setCheckinText(next);
    saveProgress(completedAssignments, completedCheckins, notes, next);
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    setUser(null);
    setCompletedAssignments({});
    setCompletedCheckins({});
    setNotes({});
    setCheckinText({});
  }

  const mod = MODULES[activeModule];

  const moduleProgress = (modIdx) => {
    const m = MODULES[modIdx];
    const done = m.assignments.filter((_, i) => completedAssignments[`${m.id}-${i}`]).length;
    return Math.round((done / m.assignments.length) * 100);
  };

  const overallProgress = () => {
    const total = MODULES.reduce((acc, m) => acc + m.assignments.length, 0);
    const done = MODULES.reduce((acc, m) =>
      acc + m.assignments.filter((_, i) => completedAssignments[`${m.id}-${i}`]).length, 0);
    return Math.round((done / total) * 100);
  };

  const tabs = ["overview", "lessons", "assignments", "check-in"];

  // ── Loading state ──
  if (!authChecked) {
    return (
      <div style={{ minHeight: "100vh", background: "#F7F7F4", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#A8C400", fontFamily: "Georgia, serif", fontSize: 18 }}>✦</div>
      </div>
    );
  }

  // ── Auth gate ──
  if (!user) return <AuthScreen onAuth={setUser} />;

  // ── Main app ──
  return (
    <div style={{
      minHeight: "100vh",
      background: "#F7F7F4",
      fontFamily: "Georgia, serif", color: "#2D2D2D",
    }}>
      {/* ── HEADER ── */}
      <div style={{
        background: "#1C1C1A",
        borderBottom: "1px solid #A8C40033",
        padding: "20px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 4, color: "#A8C400", fontWeight: "bold", marginBottom: 4 }}>THE LIFE DESIGN</div>
          <div style={{ fontSize: 20, color: "#FFFFFF", fontWeight: "bold" }}>Course Portal</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#6B6B65", letterSpacing: 2, marginBottom: 6 }}>OVERALL PROGRESS</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 140, height: 6, background: "#2C2C28", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${overallProgress()}%`, height: "100%", background: "linear-gradient(90deg, #A8C400, #D6E84A)", borderRadius: 3, transition: "width 0.5s ease" }} />
              </div>
              <div style={{ fontSize: 16, color: "#A8C400", fontWeight: "bold" }}>{overallProgress()}%</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, paddingLeft: 24, borderLeft: "1px solid #333330" }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "#AAA", marginBottom: 2 }}>{user.email}</div>
              <div style={{ fontSize: 10, color: saving ? "#A8C400" : "#666", fontStyle: "italic" }}>
                {saving ? "saving..." : "all changes saved ✓"}
              </div>
            </div>
            <button
              onClick={handleSignOut}
              style={{
                padding: "8px 16px", background: "none", border: "1px solid #444",
                borderRadius: 6, color: "#AAA", cursor: "pointer",
                fontSize: 12, fontFamily: "Georgia, serif", letterSpacing: 1,
                transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.target.style.borderColor = "#A8C400"; e.target.style.color = "#A8C400"; }}
              onMouseLeave={e => { e.target.style.borderColor = "#444"; e.target.style.color = "#AAA"; }}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", minHeight: "calc(100vh - 72px)" }}>

        {/* ── SIDEBAR ── */}
        <div style={{ width: 280, background: "#EFEFEB", borderRight: "1px solid #E0E0D8", padding: "24px 0", flexShrink: 0 }}>
          <div style={{ padding: "0 20px 16px", fontSize: 10, letterSpacing: 3, color: "#9A9A94", fontWeight: "bold" }}>6-MONTH CURRICULUM</div>
          {MODULES.map((m, i) => {
            const prog = moduleProgress(i);
            const isActive = activeModule === i;
            const checkinDone = completedCheckins[m.id];
            return (
              <div key={m.id}
                onClick={() => { setActiveModule(i); setActiveTab("overview"); }}
                style={{
                  padding: "14px 20px", cursor: "pointer",
                  background: isActive ? `linear-gradient(90deg, ${m.accent}22, transparent)` : "transparent",
                  borderLeft: isActive ? `3px solid ${m.accent}` : "3px solid transparent",
                  transition: "all 0.2s",
                }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>{m.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: isActive ? m.accent : "#9A9A94", letterSpacing: 1, fontWeight: "bold" }}>
                      MODULE {m.id} {checkinDone && "✓"}
                    </div>
                    <div style={{ fontSize: 13, color: isActive ? "#1C1C1A" : "#6B6B65", fontWeight: isActive ? "bold" : "normal" }}>{m.title}</div>
                  </div>
                </div>
                <div style={{ height: 3, background: "#D8D8D0", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: `${prog}%`, height: "100%", background: m.accent, borderRadius: 2, transition: "width 0.4s ease" }} />
                </div>
                <div style={{ fontSize: 10, color: "#9A9A94", marginTop: 3 }}>{prog}% complete</div>
              </div>
            );
          })}
        </div>

        {/* ── MAIN CONTENT ── */}
        <div style={{ flex: 1, overflow: "auto" }}>

          {/* Module Header */}
          <div style={{
            background: `linear-gradient(135deg, ${mod.color}EE 0%, ${mod.color}AA 100%)`,
            padding: "36px 40px 28px",
            borderBottom: `1px solid ${mod.accent}44`,
          }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: "rgba(255,255,255,0.75)", marginBottom: 8, fontWeight: "bold" }}>
              MODULE {mod.id}  ·  {mod.weeks}
            </div>
            <div style={{ fontSize: 32, fontWeight: "bold", color: "#FFF", marginBottom: 6, lineHeight: 1.2 }}>{mod.title}</div>
            <div style={{ fontSize: 18, color: "#FFFFFFCC", fontStyle: "italic", marginBottom: 12 }}>{mod.subtitle}</div>
            <div style={{ fontSize: 13, color: "#FFFFFFAA", letterSpacing: 1 }}>Focus: {mod.focus}</div>
          </div>

          {/* Tabs */}
          <div style={{ background: "#FFFFFF", borderBottom: "1px solid #E0E0D8", display: "flex" }}>
            {tabs.map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{
                padding: "14px 28px", background: "none", border: "none",
                borderBottom: activeTab === t ? `2px solid ${mod.accent}` : "2px solid transparent",
                color: activeTab === t ? mod.accent : "#6B6B65",
                fontSize: 12, letterSpacing: 2, fontWeight: "bold", cursor: "pointer",
                fontFamily: "Georgia, serif", textTransform: "uppercase", transition: "all 0.2s",
              }}>
                {t === "check-in" ? "Check-In" : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{ padding: "32px 40px", maxWidth: 860 }}>

            {/* ── OVERVIEW ── */}
            {activeTab === "overview" && (
              <div>
                <div style={{ background: "#FFFFFF", borderLeft: `4px solid ${mod.accent}`, padding: "20px 24px", borderRadius: "0 8px 8px 0", marginBottom: 28 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: mod.accent, fontWeight: "bold", marginBottom: 8 }}>MODULE DESCRIPTION</div>
                  <p style={{ fontSize: 15, lineHeight: 1.7, color: "#1C1C1A", margin: 0 }}>{mod.description}</p>
                </div>
                <div style={{ marginBottom: 28 }}>
                  <div style={{ fontSize: 11, letterSpacing: 3, color: mod.accent, fontWeight: "bold", marginBottom: 16 }}>COURSE OBJECTIVES</div>
                  {mod.objectives.map((obj, i) => (
                    <div key={i} style={{ display: "flex", gap: 14, marginBottom: 12, alignItems: "flex-start" }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#F7F7F4", border: `2px solid ${mod.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: mod.accent, fontWeight: "bold", flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                      <p style={{ fontSize: 14, lineHeight: 1.6, color: "#1C1C1A", margin: 0 }}>{obj}</p>
                    </div>
                  ))}
                </div>
                <div style={{ background: "#F7F7F4", border: `1px solid ${mod.accent}44`, borderRadius: 8, padding: "20px 24px" }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: mod.accent, fontWeight: "bold", marginBottom: 12 }}>MONTH AT A GLANCE</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {mod.lessons.map((l) => (
                      <div key={l.week} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <div style={{ fontSize: 11, color: mod.accent, fontWeight: "bold", letterSpacing: 1, minWidth: 56 }}>WK {l.week}</div>
                        <div style={{ fontSize: 13, color: "#1C1C1A" }}>{l.title}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #E0E0D8", display: "flex", gap: 24 }}>
                    {[
                      [mod.assignments.length, "ASSIGNMENTS", mod.accent],
                      [mod.assignments.filter(a => a.type === "reflect").length, "REFLECTIVE", "#7A9E7E"],
                      [mod.assignments.filter(a => a.type === "act").length, "ACTION", "#C9956C"],
                      [completedCheckins[mod.id] ? "✓" : "○", "CHECK-IN", completedCheckins[mod.id] ? "#7A9E7E" : "#555"],
                    ].map(([val, label, color]) => (
                      <div key={label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 22, fontWeight: "bold", color }}>{val}</div>
                        <div style={{ fontSize: 10, color: "#666", letterSpacing: 1 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── LESSONS ── */}
            {activeTab === "lessons" && (
              <div>
                <p style={{ fontSize: 14, color: "#6B6B65", fontStyle: "italic", marginBottom: 24, lineHeight: 1.6 }}>
                  Each lesson runs for one week. Read it Monday, reflect through the week, and bring your insights to your Sunday review.
                </p>
                {mod.lessons.map((l, i) => {
                  const isExpanded = expandedLesson === `${mod.id}-${i}`;
                  return (
                    <div key={i} style={{ marginBottom: 16 }}>
                      <div onClick={() => setExpandedLesson(isExpanded ? null : `${mod.id}-${i}`)} style={{
                        background: isExpanded ? mod.bg : "#FFFFFF", border: `1px solid ${isExpanded ? mod.accent : "#E0E0D8"}`,
                        borderRadius: isExpanded ? "8px 8px 0 0" : 8, padding: "18px 24px",
                        cursor: "pointer", display: "flex", alignItems: "center", gap: 16, transition: "all 0.2s",
                      }}>
                        <div style={{ width: 48, height: 48, borderRadius: "50%", background: mod.accent + "22", border: `2px solid ${mod.accent}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: "bold", color: mod.accent }}>WK{l.week}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 15, fontWeight: "bold", color: "#1C1C1A" }}>{l.title}</div>
                          <div style={{ fontSize: 12, color: "#6B6B65", marginTop: 2 }}>Week {l.week} · Module {mod.id}</div>
                        </div>
                        <div style={{ color: mod.accent, fontSize: 18 }}>{isExpanded ? "−" : "+"}</div>
                      </div>
                      {isExpanded && (
                        <div style={{ background: mod.bg, border: `1px solid ${mod.accent}`, borderTop: "none", borderRadius: "0 0 8px 8px", padding: "28px 32px" }}>
                          {/* Lesson intro line */}
                          <p style={{ fontSize: 13, color: "#6B6B65", fontStyle: "italic", marginBottom: 24, lineHeight: 1.6, borderBottom: `1px solid ${mod.accent}33`, paddingBottom: 16 }}>{l.desc}</p>

                          {/* Full lesson content if available */}
                          {LESSONS_CONTENT[`${mod.id}-${i}`] ? (
                            <div>
                              {LESSONS_CONTENT[`${mod.id}-${i}`].sections.map((sec, si) => (
                                <div key={si} style={{ marginBottom: 28 }}>
                                  {sec.heading && (
                                    <h3 style={{ fontFamily: "Georgia, serif", fontSize: 17, fontWeight: "bold", color: "#2D2D2D", margin: "0 0 10px", lineHeight: 1.3 }}>{sec.heading}</h3>
                                  )}
                                  {sec.body && sec.body.map((para, pi) => (
                                    <p key={pi} style={{ fontSize: 14, lineHeight: 1.75, color: "#1C1C1A", margin: "0 0 10px", textAlign: "justify" }}>{para}</p>
                                  ))}
                                  {sec.keyIdea && (
                                    <div style={{ background: "#EAF2EB", borderLeft: "4px solid #7A9E7E", padding: "12px 16px", margin: "14px 0", borderRadius: "0 6px 6px 0" }}>
                                      <span style={{ fontWeight: "bold", color: "#4A7C59", fontSize: 13 }}>Key Idea: </span>
                                      <span style={{ fontSize: 13, color: "#3D3020", fontStyle: "italic", lineHeight: 1.6 }}>{sec.keyIdea}</span>
                                    </div>
                                  )}
                                  {sec.bullets && (
                                    <ul style={{ margin: "10px 0", paddingLeft: 0, listStyle: "none" }}>
                                      {sec.bullets.map((b, bi) => (
                                        <li key={bi} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start" }}>
                                          <span style={{ color: mod.accent, fontSize: 14, marginTop: 2, flexShrink: 0 }}>◆</span>
                                          <span style={{ fontSize: 13, lineHeight: 1.65, color: "#1C1C1A" }}>{b}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {sec.callout && (
                                    <div style={{ background: "#F0F6D0", borderLeft: `4px solid ${mod.accent}`, borderTop: `1px solid ${mod.accent}44`, borderBottom: `1px solid ${mod.accent}44`, padding: "14px 18px", margin: "16px 0", borderRadius: "0 6px 6px 0" }}>
                                      <div style={{ fontSize: 10, letterSpacing: 2, color: mod.accent, fontWeight: "bold", marginBottom: 6 }}>{sec.callout.label.toUpperCase()}</div>
                                      <p style={{ fontSize: 13, color: "#1C1C1A", fontStyle: "italic", lineHeight: 1.65, margin: 0 }}>{sec.callout.text}</p>
                                    </div>
                                  )}
                                  {sec.reflection && (
                                    <div style={{ background: "#F5FAE0", borderLeft: "4px solid #A8C400", padding: "14px 18px", margin: "16px 0", borderRadius: "0 6px 6px 0" }}>
                                      <div style={{ fontSize: 10, letterSpacing: 2, color: "#7A9200", fontWeight: "bold", marginBottom: 6 }}>REFLECTION PROMPT</div>
                                      <p style={{ fontSize: 13, color: "#1C1C1A", fontStyle: "italic", lineHeight: 1.65, margin: 0 }}>{sec.reflection}</p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ background: "#F5FAE0", border: "1px solid #C8DC80", borderRadius: 6, padding: "14px 18px", marginBottom: 20 }}>
                              <p style={{ fontSize: 13, color: "#5A6800", margin: 0, fontStyle: "italic" }}>Full lesson content coming soon. Check back after the next update.</p>
                            </div>
                          )}

                          {/* Notes section always visible */}
                          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${mod.accent}33` }}>
                            <div style={{ fontSize: 11, letterSpacing: 2, color: mod.accent, fontWeight: "bold", marginBottom: 8 }}>YOUR NOTES FOR THIS LESSON</div>
                            <textarea
                              value={notes[`lesson-${mod.id}-${i}`] || ""}
                              onChange={e => updateNote(`lesson-${mod.id}-${i}`, e.target.value)}
                              placeholder="Write your reflections, insights, and questions here..."
                              style={{ width: "100%", minHeight: 120, padding: "12px 16px", background: "white", border: `1px solid ${mod.accent}44`, borderRadius: 6, fontFamily: "Georgia, serif", fontSize: 13, color: "#2D2D2D", resize: "vertical", outline: "none", boxSizing: "border-box" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── ASSIGNMENTS ── */}
            {activeTab === "assignments" && (
              <div>
                <p style={{ fontSize: 14, color: "#6B6B65", fontStyle: "italic", marginBottom: 24, lineHeight: 1.6 }}>
                  Complete all 6 assignments before your module check-in. Your notes save automatically.
                </p>
                <div style={{ display: "flex", gap: 12, marginBottom: 28 }}>
                  {[["reflect", "#7A9E7E", "Reflective"], ["act", "#C9956C", "Action-Based"]].map(([type, color, label]) => (
                    <div key={type} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", background: color + "22", border: `1px solid ${color}44`, borderRadius: 20 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                      <span style={{ fontSize: 12, color, fontWeight: "bold", letterSpacing: 1 }}>{label}</span>
                    </div>
                  ))}
                </div>
                {mod.assignments.map((a, i) => {
                  const key = `${mod.id}-${i}`;
                  const done = completedAssignments[key];
                  const typeColor = a.type === "reflect" ? "#7A9E7E" : "#C9956C";
                  return (
                    <div key={i} style={{ background: done ? "#F2FAE8" : "#FFFFFF", border: `1px solid ${done ? "#A8C400" : "#E0E0D8"}`, borderRadius: 8, padding: "20px 24px", marginBottom: 14, opacity: done ? 0.85 : 1, transition: "all 0.3s" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                        <div onClick={() => toggleAssignment(mod.id, i)} style={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${done ? "#A8C400" : "#D8D8D0"}`, background: done ? "#A8C400" : "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2, transition: "all 0.2s" }}>
                          {done && <span style={{ color: "white", fontSize: 14, fontWeight: "bold" }}>✓</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                            <div style={{ fontSize: 10, letterSpacing: 1, color: typeColor, fontWeight: "bold", background: typeColor + "22", padding: "2px 10px", borderRadius: 10 }}>{a.type === "reflect" ? "Reflective" : "Action"}</div>
                            <div style={{ fontSize: 15, fontWeight: "bold", color: done ? "#9A9A94" : "#1C1C1A", textDecoration: done ? "line-through" : "none" }}>{a.title}</div>
                          </div>
                          <p style={{ fontSize: 13, lineHeight: 1.6, color: "#6B6B65", margin: "0 0 12px" }}>{a.desc}</p>
                          <div style={{ fontSize: 10, letterSpacing: 2, color: "#9A9A94", fontWeight: "bold", marginBottom: 6 }}>YOUR NOTES</div>
                          <textarea
                            value={notes[`assign-${mod.id}-${i}`] || ""}
                            onChange={e => updateNote(`assign-${mod.id}-${i}`, e.target.value)}
                            placeholder={a.type === "reflect" ? "Write your reflections here..." : "Record what you did, what happened, what you noticed..."}
                            style={{ width: "100%", minHeight: 80, padding: "10px 14px", background: "#FAFAF8", border: "1px solid #E0E0D8", borderRadius: 6, fontFamily: "Georgia, serif", fontSize: 13, color: "#2D2D2D", resize: "vertical", outline: "none", boxSizing: "border-box" }}
                          />
                          {!done && (
                            <button onClick={() => toggleAssignment(mod.id, i)} style={{ marginTop: 10, padding: "8px 20px", background: typeColor, color: "white", border: "none", borderRadius: 6, fontSize: 12, fontWeight: "bold", letterSpacing: 1, cursor: "pointer", fontFamily: "Georgia, serif" }}>
                              Mark Complete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div style={{ marginTop: 20, padding: "16px 20px", background: "#F7F7F4", border: "1px solid #E0E0D8", borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "#6B6B65" }}>Module Progress</span>
                    <span style={{ fontSize: 18, fontWeight: "bold", color: mod.accent }}>{moduleProgress(activeModule)}%</span>
                  </div>
                  <div style={{ height: 6, background: "#E0E0D8", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
                    <div style={{ width: `${moduleProgress(activeModule)}%`, height: "100%", background: mod.accent, borderRadius: 3, transition: "width 0.5s ease" }} />
                  </div>
                </div>
              </div>
            )}

            {/* ── CHECK-IN ── */}
            {activeTab === "check-in" && (
              <div>
                <div style={{ background: "#FAFFF0", border: `2px solid ${mod.accent}`, borderRadius: 8, padding: "24px 28px", marginBottom: 28 }}>
                  <div style={{ fontSize: 11, letterSpacing: 3, color: mod.accent, fontWeight: "bold", marginBottom: 12 }}>✦  MODULE {mod.id} CHECK-IN PROMPT</div>
                  <p style={{ fontSize: 15, lineHeight: 1.7, color: "#1C1C1A", margin: 0, fontStyle: "italic" }}>{mod.checkin}</p>
                </div>

                {!completedCheckins[mod.id] && moduleProgress(activeModule) < 100 && (
                  <div style={{ background: "#FFFBF0", border: "1px solid #D8D000", borderRadius: 6, padding: "12px 16px", marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span>⚠️</span>
                    <p style={{ fontSize: 13, color: "#5A5000", margin: 0, lineHeight: 1.6 }}>
                      You have {mod.assignments.length - mod.assignments.filter((_, i) => completedAssignments[`${mod.id}-${i}`]).length} assignments still remaining. Complete them all for the fullest check-in.
                    </p>
                  </div>
                )}

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#AAA", fontWeight: "bold", marginBottom: 10 }}>YOUR CHECK-IN RESPONSE (Minimum 200 words)</div>
                  <textarea
                    value={checkinText[mod.id] || ""}
                    onChange={e => updateCheckinText(mod.id, e.target.value)}
                    placeholder="Write your honest, full check-in here. Don't filter. Don't perform. Just be truthful about where you are..."
                    disabled={completedCheckins[mod.id]}
                    style={{ width: "100%", minHeight: 240, padding: "16px 20px", background: completedCheckins[mod.id] ? "#F2FAE8" : "white", border: `1px solid ${completedCheckins[mod.id] ? "#A8C400" : "#E0E0D8"}`, borderRadius: 8, fontFamily: "Georgia, serif", fontSize: 14, color: "#2D2D2D", resize: "vertical", outline: "none", lineHeight: 1.7, boxSizing: "border-box" }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <div style={{ fontSize: 11, color: "#AAA" }}>
                      {(checkinText[mod.id] || "").split(/\s+/).filter(Boolean).length} words
                      {(checkinText[mod.id] || "").split(/\s+/).filter(Boolean).length < 200 && !completedCheckins[mod.id] &&
                        <span style={{ color: "#A8C400" }}> · {200 - (checkinText[mod.id] || "").split(/\s+/).filter(Boolean).length} more to go</span>
                      }
                    </div>
                    {completedCheckins[mod.id] && <div style={{ fontSize: 11, color: "#7A9200", fontWeight: "bold" }}>✓ Check-in complete</div>}
                  </div>
                </div>

                {!completedCheckins[mod.id] && (
                  <button
                    onClick={() => {
                      const wordCount = (checkinText[mod.id] || "").split(/\s+/).filter(Boolean).length;
                      if (wordCount >= 200) completeCheckin(mod.id);
                    }}
                    style={{
                      padding: "14px 32px",
                      background: (checkinText[mod.id] || "").split(/\s+/).filter(Boolean).length >= 200
                        ? `linear-gradient(135deg, ${mod.accent}, ${mod.color})` : "#E0E0D8",
                      color: (checkinText[mod.id] || "").split(/\s+/).filter(Boolean).length >= 200 ? "#1C1C1A" : "#9A9A94",
                      border: "none", borderRadius: 8, fontSize: 13, fontWeight: "bold",
                      letterSpacing: 2, cursor: "pointer", fontFamily: "Georgia, serif", transition: "all 0.2s",
                    }}>
                    COMPLETE MODULE {mod.id} CHECK-IN
                  </button>
                )}

                {completedCheckins[mod.id] && activeModule < MODULES.length - 1 && (
                  <div style={{ background: "#F2FAE8", border: "1px solid #A8C400", borderRadius: 8, padding: "20px 24px", marginTop: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: "bold", color: "#5A6800", marginBottom: 8 }}>Module {mod.id} Complete ✓</div>
                    <p style={{ fontSize: 13, color: "#5A6800", margin: "0 0 14px", lineHeight: 1.6 }}>
                      Ready to begin Module {mod.id + 1}: {MODULES[activeModule + 1].title}.
                    </p>
                    <button onClick={() => { setActiveModule(activeModule + 1); setActiveTab("overview"); }} style={{ padding: "10px 24px", background: "#7A9200", color: "white", border: "none", borderRadius: 6, fontSize: 12, fontWeight: "bold", letterSpacing: 1, cursor: "pointer", fontFamily: "Georgia, serif" }}>
                      BEGIN MODULE {mod.id + 1} →
                    </button>
                  </div>
                )}

                {completedCheckins[mod.id] && activeModule === MODULES.length - 1 && (
                  <div style={{ background: "linear-gradient(135deg, #2D2010, #F7F7F4)", border: "2px solid #A8C400", borderRadius: 12, padding: "32px 36px", marginTop: 20, textAlign: "center" }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
                    <div style={{ fontSize: 22, fontWeight: "bold", color: "#D6E84A", marginBottom: 8 }}>You've Graduated.</div>
                    <p style={{ fontSize: 14, color: "#A8C400", fontStyle: "italic", lineHeight: 1.7, margin: 0 }}>
                      Six months. Six modules. Thirty-six assignments. One full transformation. She's not coming — she's here.
                    </p>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}